import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CONNECTION_STATUS, chronicleDeviceStateAtom } from '@/atoms/chronicle';
import { checkFirmwareUpdate } from '@/tauri/chronicle/firmware-catalog';
import {
  trackNativeFirmwareUpToDate,
  trackNativeFirmwareUpdateAvailable,
  trackNativeFirmwareUpdateCheckFailed,
  trackNativeFirmwareUpdateCheckStart,
} from '@/utils/analytics/track-hardware-events';
import { extractDeviceModel } from '@/utils/chronicle/device-helpers';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

/**
 * Hook that checks Firebase Remote Config for available firmware updates
 * when the Chronicle device is connected.
 *
 * Runs once on mount when the device is connected and has a firmware version.
 * Updates the device state atom with update availability, type, and download URL.
 */
export function useChronicleFirmwareCheck() {
  const deviceState = useAtomValue(chronicleDeviceStateAtom);
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  /** Track which firmwareVersion we last checked to re-check when it changes */
  const lastCheckedVersionRef = useRef<string | null>(null);

  const isConnected = deviceState.connectionStatus === CONNECTION_STATUS.CONNECTED;
  const firmwareVersion = deviceState.firmwareVersion;
  const deviceName = deviceState.deviceName;

  const deviceModel = extractDeviceModel(deviceName);

  const checkForUpdate = useCallback(async () => {
    if (!firmwareVersion) return;

    setIsChecking(true);
    setCheckError(null);
    trackNativeFirmwareUpdateCheckStart({
      currentVersion: firmwareVersion,
      deviceModel: deviceModel ?? '',
    });

    try {
      const result = await checkFirmwareUpdate(firmwareVersion, deviceModel);
      setDeviceState((prev) => ({
        ...prev,
        latestFirmwareVersion: result.latestVersion,
        firmwareUpdateAvailable: result.updateAvailable,
        firmwareUpdateType: result.updateType,
        latestFirmwareDownloadUrl: result.downloadUrl,
        firmwareWhatsNew: result.whatsNew,
      }));

      logger.info({
        msg: '[useChronicleFirmwareCheck] Firmware check complete',
        currentVersion: firmwareVersion,
        latestVersion: result.latestVersion,
        updateAvailable: result.updateAvailable,
        updateType: result.updateType,
      });

      if (result.updateAvailable && result.latestVersion) {
        trackNativeFirmwareUpdateAvailable({
          currentVersion: firmwareVersion,
          availableVersion: result.latestVersion,
          updateType: result.updateType === 'required' ? 'forced' : 'recommended',
          deviceModel: deviceModel ?? '',
        });
      } else {
        trackNativeFirmwareUpToDate({
          currentVersion: firmwareVersion,
          deviceModel: deviceModel ?? '',
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setCheckError(message);
      logger.error({
        msg: '[useChronicleFirmwareCheck] Firmware check failed',
        error: message,
      });
      trackNativeFirmwareUpdateCheckFailed({
        errorType: error instanceof Error ? error.name : 'UNKNOWN',
        errorDescription: message,
      });
    } finally {
      setIsChecking(false);
    }
  }, [firmwareVersion, deviceModel, setDeviceState]);

  // Auto-check when connected with a valid firmware version.
  // Re-checks if firmwareVersion changes (e.g., "unknown" → "1.5" during connection).
  useEffect(() => {
    if (!isConnected) {
      lastCheckedVersionRef.current = null;
      return;
    }

    const checkKey = `${deviceModel ?? ''}:${firmwareVersion}`;
    if (
      firmwareVersion &&
      firmwareVersion !== 'unknown' &&
      checkKey !== lastCheckedVersionRef.current
    ) {
      lastCheckedVersionRef.current = checkKey;
      void checkForUpdate();
    }
  }, [isConnected, firmwareVersion, deviceModel, checkForUpdate]);

  return {
    isChecking,
    checkError,
    checkForUpdate,
  };
}
